import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchComponent from "../../components/search";
import Table from "../../components/datatable/datatable";
import { useNavigate } from "react-router-dom";
import {
  getAllUserInvoices,
  customerFetchPartnerInvoices,
  customerDownloadPartnerInvoicePdf,
} from "../../core/axios/endpoints";
import { useUser } from "../../core/context/user";
import { RoutesFullPath } from "../../utils/constants";

const fmtEUR = (n) =>
  Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");

const PARTNER_STATUS_LABELS = {
  sent: { label: "Open", color: "bg-amber-100 text-amber-800" },
  overdue: { label: "Achterstallig", color: "bg-rose-100 text-rose-800" },
  paid: { label: "Betaald", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Geannuleerd", color: "bg-slate-100 text-slate-700" },
};

const ClientFacturenPage = () => {
  const { userData } = useUser();
  const isBtwPartner = !!userData?.btwPartnerEnabled;
  const navigate = useNavigate();

  // Tab: 'subsidie' (default) of 'partner'. Alleen zichtbaar voor partners.
  const [tab, setTab] = useState("subsidie");
  const [data, setData] = useState({ data: [], totalDataCount: 0 });
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const subsidieColumns = useMemo(
    () => [
      {
        title: "Factuur Nummer",
        dataIndex: "InvoiceId",
        render: (text) => <span>{text}</span>,
      },
      {
        title: "Regeling",
        dataIndex: "application",
        render: (application) => <span>{application?.regling ?? "-"}</span>,
      },
      {
        title: "Betaald Op",
        dataIndex: "betaaldDateTime",
        render: (text) => <span>{fmtDate(text)}</span>,
      },
      {
        title: "Totaal",
        dataIndex: "application",
        render: (application) => <span>&euro;{fmtEUR(application?.totaal)}</span>,
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (text) => <span>{text}</span>,
      },
    ],
    [],
  );

  const partnerColumns = useMemo(
    () => [
      {
        title: "Factuurnummer",
        dataIndex: "invoiceNumber",
        render: (text) => <span className="font-mono text-sm">{text}</span>,
      },
      {
        title: "Type",
        dataIndex: "type",
        render: (v) => (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              v === "credit_note"
                ? "bg-rose-100 text-rose-800"
                : "bg-slate-100 text-slate-700"
            }`}
          >
            {v === "credit_note" ? "Creditnota" : "Factuur"}
          </span>
        ),
      },
      {
        title: "Datum",
        dataIndex: "invoiceDate",
        render: (v) => <span>{fmtDate(v)}</span>,
      },
      {
        title: "Kenmerk",
        dataIndex: "kenmerk",
        render: (v) => <span className="text-sm text-slate-600">{v}</span>,
      },
      {
        title: "Totaal (incl. btw)",
        dataIndex: "totalIncl",
        render: (v) => (
          <span className="font-medium">&euro;{fmtEUR(v)}</span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (v) => {
          const cfg =
            PARTNER_STATUS_LABELS[v] ?? PARTNER_STATUS_LABELS.sent;
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.color}`}
            >
              {cfg.label}
            </span>
          );
        },
      },
      {
        title: "PDF",
        dataIndex: "_id",
        render: (id) => (
          <button
            type="button"
            onClick={async (e) => {
              e.stopPropagation();
              if (downloadingId) return;
              setDownloadingId(id);
              try {
                const blob = await customerDownloadPartnerInvoicePdf(id);
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `factuur-${id}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                console.error("PDF download mislukt:", err);
              } finally {
                setDownloadingId(null);
              }
            }}
            className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-gray-900 hover:border-emerald-500 disabled:opacity-50"
            disabled={downloadingId === id}
          >
            {downloadingId === id ? "Bezig..." : "Download"}
          </button>
        ),
      },
    ],
    [downloadingId],
  );

  const fetchData = useCallback(
    async ({ page, pageSize, resetPageSize }, searchText = "") => {
      setLoading(true);
      try {
        if (tab === "partner") {
          const body = await customerFetchPartnerInvoices({
            page,
            limit: pageSize,
          });
          setData({
            data: (body?.docs ?? []).map((item, i) => ({ ...item, id: i })),
            totalDataCount: body?.pagination?.totalDocs ?? 0,
            resetPageSize,
          });
        } else {
          const body = await getAllUserInvoices(page, pageSize, searchText);
          setData({
            data: (body?.docs ?? []).map((item, i) => ({ ...item, id: i })),
            totalDataCount: body?.totalDocs ?? 0,
            resetPageSize,
          });
        }
      } catch (e) {
        console.error("Facturen ophalen mislukt:", e);
      } finally {
        setLoading(false);
      }
    },
    [tab],
  );

  useEffect(() => {
    fetchData({ page: 1, pageSize: 10, resetPageSize: true });
  }, [tab]);

  const handleRowClick = (record) => {
    if (tab === "subsidie" && record?._id) {
      navigate(`${RoutesFullPath.BetalingDetails}/${record?._id}`);
    }
    // partner-facturen: detail-view komt later, voor nu doet rij-klik niets.
  };

  const handleSearch = (query) => {
    if (tab !== "subsidie") return; // partner-list heeft geen search in V1
    fetchData({ page: 1, pageSize: 10, resetPageSize: true }, query);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="p-4 space-y-4">
          {isBtwPartner ? (
            <div className="flex gap-2 border-b border-border">
              <button
                type="button"
                onClick={() => setTab("subsidie")}
                className={`px-4 py-2 text-sm font-medium transition ${
                  tab === "subsidie"
                    ? "border-b-2 border-gray-900 text-gray-900"
                    : "text-slate-500 hover:text-gray-900"
                }`}
              >
                Subsidie-facturen
              </button>
              <button
                type="button"
                onClick={() => setTab("partner")}
                className={`px-4 py-2 text-sm font-medium transition ${
                  tab === "partner"
                    ? "border-b-2 border-gray-900 text-gray-900"
                    : "text-slate-500 hover:text-gray-900"
                }`}
              >
                BTW Partner-facturen
              </button>
            </div>
          ) : null}

          {tab === "subsidie" ? (
            <SearchComponent onSearch={handleSearch} type={"invoices"} />
          ) : null}
        </div>
        <div className="table-responsive">
          <Table
            columns={tab === "subsidie" ? subsidieColumns : partnerColumns}
            response={data}
            fetchData={fetchData}
            onRowClick={handleRowClick}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientFacturenPage;
